/**
 * WalletConnect v2, for phones and browsers with no extension.
 *
 * `@walletconnect/ethereum-provider` is a large chunk graph that must never be
 * in the boot bundle: it is reached only through `defaultFactory`'s dynamic
 * import, and nothing is created until the user picks WalletConnect (or picked
 * it last time). A provider existing is not a session existing: callers ask
 * `hasWalletConnectSession()`.
 *
 * `runtimeConfig` is imported lazily: `runtimeConfig -> api -> auth -> wallet`
 * is a cycle, and a static edge from here would run it through `wallet.ts`,
 * whose module body installs the EIP-6963 listener.
 */

import { storageKeys, timing } from "../config";
import { getChains } from "./chainSelect";
import type { Eip1193Provider } from "./wallet";

/**
 * Synthetic EIP-6963 rdns for the WalletConnect entry. Namespaced under the
 * app so no injected extension can ever announce it and hijack the pin; it is
 * what lands in localStorage under `aile.wallet.rdns`.
 */
export const WALLETCONNECT_RDNS = "aile.walletconnect";

/** UI copy for the picker row and the primary button. */
export const WALLETCONNECT_NAME = "WalletConnect";
export const WALLETCONNECT_HINT = "Scan with your phone";

/** Inline: the bundle ships no external images. */
export const WALLETCONNECT_ICON =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3E%3Crect width='32' height='32' rx='8' fill='%233b99fc'/%3E%3Cpath d='M9.6 12.6c3.5-3.5 9.3-3.5 12.8 0l.4.4a.44.44 0 0 1 0 .63l-1.44 1.41a.23.23 0 0 1-.32 0l-.58-.57a6.3 6.3 0 0 0-8.82 0l-.62.61a.23.23 0 0 1-.32 0L9.26 13.7a.44.44 0 0 1 0-.63zm15.8 2.96 1.28 1.26a.44.44 0 0 1 0 .63l-5.78 5.67a.46.46 0 0 1-.64 0l-4.1-4.03a.11.11 0 0 0-.16 0l-4.1 4.03a.46.46 0 0 1-.64 0L5.48 17.45a.44.44 0 0 1 0-.63l1.28-1.26a.46.46 0 0 1 .64 0l4.1 4.02c.05.05.12.05.16 0l4.1-4.02a.46.46 0 0 1 .64 0l4.1 4.02c.05.05.12.05.16 0l4.1-4.02a.46.46 0 0 1 .64 0z' fill='%23fff'/%3E%3C/svg%3E";

/**
 * Synthetic codes for the three WalletConnect outcomes that have no EIP-1193
 * equivalent. Each is carried on a `WalletError` whose message is already
 * finished user-facing English, and `explainWalletError` renders it verbatim.
 */
export const WALLETCONNECT_UNAVAILABLE = "WALLETCONNECT_UNAVAILABLE";
export const WALLETCONNECT_TIMEOUT = "WALLETCONNECT_TIMEOUT";
export const WALLETCONNECT_EXPIRED = "WALLETCONNECT_EXPIRED";

/** WalletConnect v2's JSON-RPC code for "the user rejected this". */
export const WALLETCONNECT_USER_REJECTED = 5000;

/**
 * `initWalletConnect()` could not hand back a provider. "unconfigured": no
 * project id, permanent, so a pin-only connect may forget the pin. "closed": a
 * Cancel or sign-out overtook the init, a user choice that keeps the pin.
 */
export class WalletConnectUnavailable extends Error {
  readonly code = WALLETCONNECT_UNAVAILABLE;
  readonly reason: "unconfigured" | "closed";

  constructor(message: string, reason: "unconfigured" | "closed" = "unconfigured") {
    super(message);
    this.name = "WalletConnectUnavailable";
    this.reason = reason;
  }
}

/** The bits of `EthereumProvider` this app uses, on top of EIP-1193. */
export interface WalletConnectProvider extends Eip1193Provider {
  /** connect-if-needed + return accounts. Not `eth_requestAccounts`. */
  enable(): Promise<string[]>;
  disconnect(): Promise<void>;
  /** Undefined until a session exists (restored from storage or freshly paired). */
  readonly session: unknown;
  /**
   * The QR modal. The library closes it only from `connect()`'s `finally`
   * (see `closeWalletConnectModal`). Absent with `showQrModal: false`.
   */
  readonly modal?: AppKitModal | undefined;
  /**
   * The chain every request is relayed for (`EthereumProvider.request` sends
   * `"eip155:" + this.chainId`), whatever the session approved. Absent in fakes.
   */
  readonly chainId?: number | undefined;
  /** Approved accounts on the current chain, `[]` before pairing. Absent in fakes. */
  readonly accounts?: readonly string[] | undefined;
  /**
   * The UniversalProvider underneath. Its `connect` is emitted only at the end
   * of its own `pair()`, so it tells that a dropped provider paired anyway.
   */
  readonly signer?:
    | {
        on?(event: "connect", listener: () => void): unknown;
        removeListener?(event: "connect", listener: () => void): unknown;
      }
    | undefined;
}

/**
 * The slice of AppKit (`provider.modal`, typed `any`) used here.
 * `subscribeState` watches module-global state, shared by every AppKit
 * instance in the tab. Optional: most fakes carry `close` alone.
 */
export interface AppKitModal {
  close(): unknown;
  updateOptions?(options: Record<string, unknown>): void;
  subscribeState?(callback: (state: { open?: boolean }) => void): () => void;
}

/** Seam for tests: jsdom has no usable WebSocket/crypto for the real package. */
export type WalletConnectFactory = (opts: {
  projectId: string;
  chainId: number;
}) => Promise<WalletConnectProvider>;

/**
 * Exactly one chain, the deployment's: with another approved chain, a wallet's
 * `chainChanged` would move every read and write there. The offer is not
 * enough on its own (a wallet may approve its own chains too, and `connect()`
 * starts on the first account's chain), so `pinToDeploymentChain` holds the
 * provider on this chain and `ensureWalletConnectChain` refuses any other.
 */
function offeredChainIds(chainId: number): [number, ...number[]] {
  return [chainId];
}

/**
 * Read endpoints, one per `chains.json` network that names a wallet RPC.
 * `eth_call` and receipts are answered over HTTPS, and a chain missing here
 * would go to Reown's Blockchain API on our project id, a third-party
 * dependency with its own quota.
 */
function rpcMap(): Record<number, string> {
  const map: Record<number, string> = {};
  for (const entry of getChains()) {
    const url = entry.walletRpcUrls?.[0];
    if (entry.chainId !== undefined && url !== undefined) map[entry.chainId] = url;
  }
  return map;
}

/**
 * The options `EthereumProvider.init` gets, exported so a test can assert the
 * session proposal: the tests replace `defaultFactory` whole and never load the
 * package. AppKit's `allowUnsupportedChain` cannot be set here
 * (`convertWCMToAppKitOptions` drops it); see `allowUnsupportedChain`.
 */
export function walletConnectInitOptions({
  projectId,
  chainId,
}: {
  projectId: string;
  chainId: number;
}) {
  const origin = window.location.origin;
  return {
    projectId,
    optionalChains: offeredChainIds(chainId),
    optionalMethods: [
      "personal_sign",
      "eth_signTypedData_v4",
      "eth_sendTransaction",
      "wallet_switchEthereumChain",
    ],
    rpcMap: rpcMap(),
    showQrModal: true,
    metadata: {
      name: "Aile",
      description: "Intent-based FX stablecoin swaps",
      // Must equal the live origin: Reown's verify service cross-checks it and
      // wallets show a red "domain mismatch" scam warning when it differs.
      url: origin,
      icons: [`${origin}/aile-logo.svg`],
    },
  };
}

/**
 * Turns AppKit's `allowUnsupportedChain` on, after every init, orphans too:
 * each `createAppKit` resets that global option synchronously, so the last
 * writer must come after `init()` resolves. Called from `initWalletConnect`, so
 * the test factory goes through it as well.
 *
 * Without it (as of @reown/appkit 1.8.19), a wallet that re-announces its own
 * chain (MetaMask's `chainChanged eip155:1`) makes AppKit open its
 * unsupported-chain view, which under `manualWCControl` is the QR sheet, over a
 * signed-in app. `guardModal` is the backstop for any other path to `open`.
 */
function allowUnsupportedChain(p: WalletConnectProvider): void {
  try {
    p.modal?.updateOptions?.({ allowUnsupportedChain: true });
  } catch {
    // An AppKit without it: the modal guard still closes a stray sheet.
  }
}

const defaultFactory: WalletConnectFactory = async ({ projectId, chainId }) => {
  const { EthereumProvider } = await import("@walletconnect/ethereum-provider");
  const provider = await EthereumProvider.init(walletConnectInitOptions({ projectId, chainId }));
  // `Eip1193Provider` types listeners as `(...args: never[]) => void`, which no
  // declared provider class satisfies; casting here keeps the injected-wallet
  // call sites strict.
  return provider as unknown as WalletConnectProvider;
};

let factory: WalletConnectFactory = defaultFactory;
let provider: WalletConnectProvider | null = null;
let initInFlight: Promise<WalletConnectProvider> | null = null;
/**
 * Bumped by every teardown. An init whose captured generation is stale drops
 * its provider, or a sign-out during an init would be undone when it lands.
 */
let generation = 0;
const disconnectListeners = new Set<() => void>();

function notifyDisconnected(): void {
  for (const listener of [...disconnectListeners]) listener();
}

/**
 * Fires when the wallet ends the pairing from the phone. Registered on this
 * provider, not through wallet.ts `subscribe()`, which binds once at subscribe
 * time, on a desktop to the injected wallet, whose `disconnect` is a network
 * blip, not a sign-out.
 */
export function onWalletConnectDisconnected(handler: () => void): () => void {
  disconnectListeners.add(handler);
  return () => {
    disconnectListeners.delete(handler);
  };
}

/* ------------------------------------------------------------ chain pinning */

/** The deployment chain each provider was created for, and its listener. */
const chainPins = new WeakMap<
  WalletConnectProvider,
  { chainId: number; listener: (raw: unknown) => void }
>();

function parseChainId(raw: unknown): number | null {
  if (typeof raw === "number") return raw;
  if (typeof raw !== "string") return null;
  const parsed = raw.startsWith("0x") ? Number.parseInt(raw, 16) : Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Does the session hold an account on `chainId`? Mirrors universal-provider
 * 2.25.0 `isChainApproved` (the accounts of every key containing "eip155"): a
 * switch the library does not consider approved is relayed to the wallet as a
 * real prompt. An unreadable session is not approved.
 */
function sessionApprovesChain(p: WalletConnectProvider, chainId: number): boolean {
  const namespaces = (
    p.session as { namespaces?: Record<string, { accounts?: unknown }> } | undefined | null
  )?.namespaces;
  if (namespaces === undefined || namespaces === null) return false;
  const prefix = `eip155:${chainId}:`;
  return Object.entries(namespaces).some(
    ([key, value]) =>
      key.includes("eip155") &&
      Array.isArray(value?.accounts) &&
      value.accounts.some((a) => typeof a === "string" && a.startsWith(prefix)),
  );
}

/**
 * Moves the provider back onto `chainId` without a wallet prompt: in
 * universal-provider 2.25.0 a switch to an approved chain only calls
 * `setDefaultChain`, synchronously, while an unapproved one is relayed to the
 * wallet, hence null without approval. It moves both the EthereumProvider's
 * chain id (relayed requests) and the Eip155Provider's (`eth_chainId`, reads),
 * which is why the boot pin switches even when the first already looks right.
 */
function switchLocally(p: WalletConnectProvider, chainId: number): Promise<unknown> | null {
  if (!sessionApprovesChain(p, chainId)) return null;
  return p.request({
    method: "wallet_switchEthereumChain",
    params: [{ chainId: `0x${chainId.toString(16)}` }],
  });
}

/**
 * Holds a live provider on the deployment's chain. `connect()`, a wallet's
 * `chainChanged` and a restored session's persisted chain all surface as the
 * provider's `chainChanged`, so one listener switches straight back, locally;
 * the switch back emits this chain, which it ignores. It runs inside the
 * library's emit on purpose: deferring would let a request out on the wrong
 * chain. `provider.accounts` may then list the other chain's accounts, the same
 * address for an EOA. `ensureWalletConnectChain` checks again before every
 * request.
 */
function pinToDeploymentChain(p: WalletConnectProvider, chainId: number): void {
  const listener = (raw: unknown): void => {
    // Only for the provider the app is using; a dropped one is being hung up.
    if (provider !== p) return;
    const now = parseChainId(raw);
    if (now === null || now === chainId) return;
    void switchLocally(p, chainId)?.catch(() => undefined);
  };
  chainPins.set(p, { chainId, listener });
  p.on?.("chainChanged", listener);
  // A restored session: its persisted chain was read before we listened.
  if (hasWalletConnectSession(p)) void switchLocally(p, chainId)?.catch(() => undefined);
}

function unpinChain(p: WalletConnectProvider): void {
  const pin = chainPins.get(p);
  if (pin === undefined) return;
  p.removeListener?.("chainChanged", pin.listener);
  chainPins.delete(p);
}

/**
 * True when `p` may send now: on the deployment's chain, or switched back
 * locally. False means refuse rather than sign for another chain. Never
 * prompts; a provider with no numeric `chainId` or no pin (fakes) passes.
 */
export async function ensureWalletConnectChain(p: WalletConnectProvider): Promise<boolean> {
  const want = chainPins.get(p)?.chainId;
  if (want === undefined || typeof p.chainId !== "number") return true;
  if (p.chainId === want) return true;
  const switching = switchLocally(p, want);
  if (switching !== null) await switching.catch(() => undefined);
  return p.chainId === want;
}

/* ------------------------------------------------------------- modal guard */

/** `enable()` calls in flight: the QR sheet belongs on screen only during one. */
let pairingDepth = 0;
/** Unsubscribes the one modal guard; see `guardModal`. */
let modalGuardOff: (() => void) | null = null;

/**
 * Closes the QR sheet whenever it opens with no pairing in flight: under
 * `manualWCControl` (@reown/appkit-controllers 1.8.19) any `ModalController.open`
 * shows the QR sheet over whatever page is open, and `allowUnsupportedChain`
 * removes only the known path. It only ever closes. Exactly one, dropped on
 * teardown: `subscribeState` is global, so a stale guard would act on the next
 * provider's modal.
 */
function guardModal(p: WalletConnectProvider): void {
  const modal = p.modal;
  modalGuardOff?.();
  modalGuardOff = null;
  if (modal?.subscribeState === undefined) return;
  try {
    modalGuardOff = modal.subscribeState((state) => {
      if (state.open !== true || pairingDepth > 0) return;
      try {
        void Promise.resolve(modal.close()).catch(() => undefined);
      } catch {
        // Nothing to close.
      }
    });
  } catch {
    modalGuardOff = null;
  }
}

function unguardModal(): void {
  modalGuardOff?.();
  modalGuardOff = null;
}

/**
 * The only way wallet.ts may call `enable()`: it marks a pairing as in flight
 * for the modal guard, which would otherwise close the QR sheet the moment it
 * opened.
 */
export async function enableWalletConnect(p: WalletConnectProvider): Promise<string[]> {
  pairingDepth += 1;
  try {
    return await p.enable();
  } finally {
    pairingDepth -= 1;
  }
}

/** Tests only: install a fake provider factory. */
export function setWalletConnectFactory(next: WalletConnectFactory | null): void {
  factory = next ?? defaultFactory;
}

/** The live provider, or null when nothing has been created yet. */
export function getWalletConnectProvider(): WalletConnectProvider | null {
  return provider;
}

/** True once a session exists — restored from storage or freshly paired. */
export function hasWalletConnectSession(p: WalletConnectProvider): boolean {
  return p.session !== undefined && p.session !== null;
}

/**
 * Creates the provider once. `init()` may rehydrate a stored session with no
 * prompt: check `hasWalletConnectSession()`. Not `async`, so the guard and the
 * `initInFlight` assignment run in one synchronous step and callers cannot race.
 */
export function initWalletConnect(): Promise<WalletConnectProvider> {
  if (provider !== null) return Promise.resolve(provider);
  if (initInFlight !== null) return initInFlight;
  const mine = generation;
  const pending = (async () => {
    const { getChainId, getWalletConnectProjectId } = await import("./runtimeConfig");
    const projectId = getWalletConnectProjectId();
    if (projectId === null) {
      throw new WalletConnectUnavailable("WalletConnect is not available on this deployment");
    }
    const chainId = getChainId();
    if (chainId === null) {
      throw new WalletConnectUnavailable("WalletConnect is not available until the network is known");
    }
    const created = await factory({ projectId, chainId });
    // Before the generation check: an orphaned init has still reset the
    // global AppKit options (see `allowUnsupportedChain`).
    allowUnsupportedChain(created);
    if (mine !== generation) {
      // Torn down while we were initialising. Drop it rather than adopt it,
      // and hang up on the relay so the phone stops showing a live session.
      void created.disconnect().catch(() => undefined);
      throw new WalletConnectUnavailable("WalletConnect session was closed", "closed");
    }
    provider = created;
    // Only for the provider we adopt. Both are undone by the teardown.
    pinToDeploymentChain(created, chainId);
    guardModal(created);
    // `session_delete` only: one wallet-side delete emits it and `disconnect`
    // both, so listening to both would run every handler twice.
    created.on?.("session_delete", notifyDisconnected);
    return created;
  })();
  const settled: Promise<WalletConnectProvider> = pending.finally(() => {
    if (initInFlight === settled) initInFlight = null;
  });
  initInFlight = settled;
  // The orphan branch above rejects a promise nobody is awaiting any more.
  void settled.catch(() => undefined);
  return settled;
}

/**
 * Ends a session that cannot serve this browser's sign-in and tells the
 * listeners, as a phone's `session_delete` does: dropping the singleton alone
 * would not do, since wallet.ts `resolveTarget()` reads it directly.
 */
export async function abandonWalletConnect(): Promise<void> {
  await disconnectWalletConnect();
  notifyDisconnected();
}

/**
 * Set when a teardown could not reach the relay (see `disconnectWalletConnect`).
 * localStorage, not a module variable: the whole point is to survive the reload
 * or the closed tab that would otherwise rehydrate the abandoned pairing.
 */
function markUnclean(on: boolean): void {
  try {
    if (on) localStorage.setItem(storageKeys.walletConnectUnclean, "1");
    else localStorage.removeItem(storageKeys.walletConnectUnclean);
  } catch {
    // No storage at all — then nothing was persisted for us to distrust.
  }
}

/** True when the last sign-out may have left the pairing alive on the relay. */
export function walletConnectTeardownUnclean(): boolean {
  try {
    return localStorage.getItem(storageKeys.walletConnectUnclean) !== null;
  } catch {
    return false;
  }
}

/** Cleared once a pairing this browser owns has been established. */
export function clearWalletConnectUnclean(): void {
  markUnclean(false);
}

/**
 * One hang-up per dropped provider: the late-session watcher and the
 * post-`enable()` branch of wallet.ts `connectWalletConnect` can both reach
 * the same one, and a second `disconnect()` on the real provider throws.
 */
const hangUps = new WeakMap<WalletConnectProvider, Promise<void>>();
/** Hang-ups still on the relay; the next connect waits for them. */
const pendingHangUps = new Set<Promise<void>>();
/** Stops for the armed watchers, so a test reset leaves no timer behind. */
const orphanWatchers = new Set<() => void>();

/**
 * Ends the session of a provider the singleton no longer holds. Never clears
 * the unclean flag (it may be about another pairing), but a failure sets it:
 * the library publishes `wc_sessionDelete` before deleting its local record, so
 * the session stays in shared storage for the next `init()` to rehydrate.
 */
export function hangUpOrphan(p: WalletConnectProvider): Promise<void> {
  const existing = hangUps.get(p);
  if (existing !== undefined) return existing;
  const run = (async () => {
    if (!hasWalletConnectSession(p)) return;
    await p.disconnect().catch(() => undefined);
    if (hasWalletConnectSession(p)) markUnclean(true);
  })();
  hangUps.set(p, run);
  pendingHangUps.add(run);
  void run.finally(() => pendingHangUps.delete(run));
  return run;
}

/**
 * Null when no orphan hang-up is in flight, so the caller can stay synchronous;
 * otherwise resolves once every one has settled. Never rejects.
 */
export function orphanHangUpsSettled(): Promise<void> | null {
  if (pendingHangUps.size === 0) return null;
  return Promise.allSettled([...pendingHangUps]).then(() => undefined);
}

/**
 * Teardowns so far. A caller that awaits before `initWalletConnect()` compares
 * it across the await: the init captures the generation only when it starts,
 * so a teardown that lands before that would otherwise go unnoticed.
 */
export function walletConnectGeneration(): number {
  return generation;
}

/**
 * Hangs up on `p` if it pairs after we dropped it. Bound to that one object:
 * its signer's `connect` is emitted by its own `pair()` only, never by a newer
 * provider's, so this can never end the session of a later attempt.
 */
function watchForLateSession(p: WalletConnectProvider): void {
  const signer = p.signer;
  if (signer?.on === undefined) return;
  const onConnect = (): void => {
    stop();
    void hangUpOrphan(p);
  };
  const timer = setTimeout(stop, timing.wallet.walletConnectOrphanWatchMs);
  function stop(): void {
    clearTimeout(timer);
    signer?.removeListener?.("connect", onConnect);
    orphanWatchers.delete(stop);
  }
  orphanWatchers.add(stop);
  signer.on("connect", onConnect);
}

/**
 * The user backed out before pairing completed. Closing the modal is the only
 * way to unwind our side (`enable()` has no deadline, `disconnect()` does
 * nothing without a session): the provider then rejects `connect()` with
 * "Connection request reset".
 *
 * It does not stop the pairing: in universal-provider 2.25.0
 * `abortPairingAttempt()` is a no-op, and a phone approving within the
 * proposal's five-minute TTL gives the dropped provider a session in the shared
 * storage. So the caller also drops the singleton, and `watchForLateSession`
 * hangs up such a session the moment it lands. One gap remains: a late session
 * landing during the next click's own `init()` storage read is rehydrated
 * before the hang-up deletes it.
 */
export function closeWalletConnectModal(): void {
  try {
    void Promise.resolve(provider?.modal?.close()).catch(() => undefined);
  } catch {
    // No modal (showQrModal off, or init never finished) — nothing to close.
  }
}

/**
 * SIWE and the order signature. The proposal has no required namespace (a
 * wallet that cannot satisfy one refuses outright), so a wallet may grant a
 * subset. Read from the session, never `provider.namespace.methods`, which is
 * merged with what we asked for.
 */
const REQUIRED_SESSION_METHODS = ["personal_sign", "eth_signTypedData_v4"] as const;

/**
 * True when the session can sign on `chainId`. An unreadable session counts as
 * granted: refusing every login when the library changes shape would be a
 * self-inflicted outage. Per chain, as sign-client's `isValidNamespacesRequest`:
 * only entries whose accounts include the chain contribute methods (the
 * trailing colon keeps 8453 from matching 84532); an entry with no readable
 * accounts is not excluded.
 */
export function sessionGrantsSigning(p: WalletConnectProvider, chainId: number): boolean {
  const namespaces = (
    p.session as
      | { namespaces?: Record<string, { methods?: unknown; accounts?: unknown }> }
      | undefined
      | null
  )?.namespaces;
  if (namespaces === undefined || namespaces === null) return true;
  const prefix = `eip155:${chainId}:`;
  const granted = new Set<string>();
  let readable = false;
  for (const [key, value] of Object.entries(namespaces)) {
    // eip155 is the only namespace this app speaks; wallets key it either
    // per-namespace ("eip155") or per-chain ("eip155:8453").
    if (key !== "eip155" && !key.startsWith("eip155:")) continue;
    const methods = value?.methods;
    if (!Array.isArray(methods)) continue;
    // Readable before the chain filter: a session whose entries all serve
    // other chains is a readable answer ("not here"), not an unknown shape.
    readable = true;
    const accounts = value?.accounts;
    if (
      Array.isArray(accounts) &&
      !accounts.some((a) => typeof a === "string" && a.startsWith(prefix))
    ) {
      continue;
    }
    for (const method of methods) if (typeof method === "string") granted.add(method);
  }
  if (!readable) return true;
  return REQUIRED_SESSION_METHODS.every((method) => granted.has(method));
}

/**
 * Tears the pairing down and drops the singleton. Never rejects: signing out
 * must not depend on a relay round-trip, and the library throws outright when
 * asked to disconnect with no session.
 */
export async function disconnectWalletConnect(): Promise<void> {
  generation += 1; // orphans any in-flight init
  const current = provider;
  provider = null;
  initInFlight = null;
  if (current === null) return;
  current.removeListener?.("session_delete", notifyDisconnected);
  unpinChain(current);
  unguardModal();
  if (!hasWalletConnectSession(current)) {
    // Possibly mid-pairing (Cancel, or a sign-out while the QR was up): the
    // pairing keeps running after we drop it — see `closeWalletConnectModal`.
    watchForLateSession(current);
    return;
  }
  try {
    await current.disconnect();
    markUnclean(false);
  } catch {
    // The pairing may survive: SignClient publishes `wc_sessionDelete` before
    // deleting its local record, so the next `init()` would rehydrate it. The
    // flag makes a later connect hang up on it instead of resuming it.
    markUnclean(true);
  }
}

/**
 * The user closed the QR modal or declined the proposal: an outcome, not a
 * fault. Exact signals only: a keyword match like /closed|cancel/i would file
 * a dead relay ("WebSocket connection closed abnormally") as a user choice.
 */
export function isWalletConnectRejection(err: unknown): boolean {
  if (err === null || typeof err !== "object") return false;
  if ((err as { code?: unknown }).code === WALLETCONNECT_USER_REJECTED) return true;
  return isWalletConnectReset(err);
}

/**
 * The QR sheet was closed with no session (its X, or Cancel): the one rejection
 * no wallet took part in, so the UI says "cancelled", not "rejected".
 */
export function isWalletConnectReset(err: unknown): boolean {
  if (err === null || typeof err !== "object") return false;
  const message = (err as { message?: unknown }).message;
  return typeof message === "string" && message.startsWith("Connection request reset");
}

/** The session proposal timed out before the wallet answered. Its own state. */
export function isWalletConnectExpiry(err: unknown): boolean {
  if (err === null || typeof err !== "object") return false;
  const message = (err as { message?: unknown }).message;
  return typeof message === "string" && /^Proposal expired/i.test(message);
}

/** Drops provider, factory and listeners. Tests only — `resetProviderDiscovery()`. */
export function resetWalletConnect(): void {
  generation += 1;
  provider = null;
  initInFlight = null;
  factory = defaultFactory;
  disconnectListeners.clear();
  for (const stop of [...orphanWatchers]) stop();
  pendingHangUps.clear();
  // Not `pairingDepth`: an `enable()` still settling decrements it later.
  unguardModal();
  markUnclean(false);
}
